import React, { useMemo, useState, useCallback } from "react";
import FavoriteButton from "../../components/button/FavoriteButton";
import ShareButton from "../button/ShareButton";
import { getImageUrl } from "../../api";
import CountdownPreview from "../utils/CountDownPreview";
import Treno from "../../assets/icone/header/iconaTreniOut.svg";
import Single from "../../assets/icone/card/iconaSingleFill.svg";
import TrainLogo from "../../utils/TrainLogo";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PrevStation from "../../assets/station/PrevAllStation.jpg";

const TrainCard = ({ ticket }) => {
  // Stato per gestire la visualizzazione del ritorno e il flip
  const [showReturnTrip, setShowReturnTrip] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const { t } = useTranslation();

  // Funzione per gestire il flip
  const handleFlip = () => {
    // Cambia lo stato per mostrare il ritorno
    setShowReturnTrip((prevState) => !prevState);

    // Attiva la rotazione della card con un ritardo
    setTimeout(() => {
      setIsFlipped((prev) => !prev);
    }, 600); // 400ms di ritardo per l'animazione
  };

  // Funzione per convertire il codice valuta in simbolo
  const convertCurrencySymbol = (currency) => {
    switch (currency.toUpperCase()) {
      case "EUR":
        return "€";
      case "USD":
        return "$";
      case "GBP":
        return "£";
      default:
        return currency;
    }
  };

  // Funzione per ottenere l'offerta più alta
  const getHighestOffer = useCallback(
    (offers) => {
      if (!offers || offers.length === 0) return null;
      return Math.max(...offers.map((offer) => offer.offer_price));
    },
    [ticket.offers]
  );

  // Funzione per calcolare la percentuale di sconto
  const calculateDiscountPercentage = useCallback(
    (maxPrice, buyNowPrice) => {
      if (!maxPrice || !buyNowPrice || maxPrice <= buyNowPrice)
        return null;
      const discount = ((maxPrice - buyNowPrice) / maxPrice) * 100;
      return Math.floor(discount);
    },
    [ticket.maxPrice, ticket.buyNowPrice]
  );

  // Funzione per calcolare la durata del viaggio
  const calculateDuration = (departure, arrival) => {
    const departureTime = new Date(departure);
    const arrivalTime = new Date(arrival);
    const diffInMs = arrivalTime - departureTime;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  // Usa useMemo per calcolare la durata del viaggio di andata e ritorno
  const travelDuration = useMemo(() => {
    if (ticket.trainDateTimeDeparture && ticket.trainDateArrivalTime) {
      return calculateDuration(
        ticket.trainDateTimeDeparture,
        ticket.trainDateArrivalTime
      );
    }
    return null;
  }, [ticket.trainDateTimeDeparture, ticket.trainDateArrivalTime]);

  const returnDuration = useMemo(() => {
    if (
      ticket.trainDateTimeReturnDeparture &&
      ticket.trainDateTimeReturnDestination
    ) {
      return calculateDuration(
        ticket.trainDateTimeReturnDeparture,
        ticket.trainDateTimeReturnDestination
      );
    }
    return null;
  }, [
    ticket.trainDateTimeReturnDeparture,
    ticket.trainDateTimeReturnDestination,
  ]);

  // Ottieni l'offerta più alta e calcola lo sconto
  const highestOffer = useMemo(
    () => getHighestOffer(ticket.offers),
    [ticket.offers]
  );

  const discountPercentage = useMemo(() => {
    return calculateDiscountPercentage(
      ticket.maxPrice,
      ticket.buyNowPrice
    );
  }, [ticket.maxPrice, ticket.buyNowPrice]);

  // Calcola il prezzo da mostrare considerando finalPrice (con coupon) o buyNowPrice + penaltyPrice
  const displayPrice = useMemo(() => {
    // Se esiste finalPrice (coupon applicato), usalo
    if (ticket.finalPrice !== undefined && ticket.finalPrice !== null) {
      return Math.floor(ticket.finalPrice);
    }
    // Altrimenti usa buyNowPrice + penaltyPrice
    const basePrice = ticket.buyNowPrice || 0;
    const penalty = ticket.penaltyPrice || 0;
    return Math.floor(basePrice + penalty);
  }, [ticket.finalPrice, ticket.buyNowPrice, ticket.penaltyPrice]);

  // Funzione per bloccare la propagazione del click sui pulsanti

  const handleFavoriteClick = useCallback((e) => {
    e.preventDefault(); // Blocca la navigazione
    e.stopPropagation(); // Ferma la propagazione del click
    // Logica per il pulsante Preferito
  }, []);
  const handleShareClick = useCallback((e) => {
    e.preventDefault(); // Blocca la navigazione
    e.stopPropagation(); // Ferma la propagazione del click
    // Logica per il pulsante Condividi
  }, []);

  const handleSwitch = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  });

  const cardUrl = `${window.location.origin}/ticket/train/${ticket._id}`;
  const imageUrl = getImageUrl(ticket.trainDestinationCity?.prevImg);
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}/${month}`;
  };

  // Funzione per ottenere lo stile di rotazione del testo
  const getTextRotationStyle = (isFlipped) => ({
    transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
    transition: "transform 0.0s ease-in-out ",
  });

  const getRotationStyle = (isFlipped) => ({
    transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
    transition: "transform 0.0s ease-in-out ",
  });
  return (
    <div
      className={`card-container-train  ${
        showReturnTrip ? "rotate-y-180" : ""
      }`}
    >
      <div
        className="flex bg-white  rounded-2xl shadow-md  h-64 sm:h-72"
        style={getRotationStyle(isFlipped)}
      >
        {/* Colonna sinistra con immagine */}
        <div className="w-1/3 h-full mr-4 flex relative">
          <div className="absolute top-5 left-0 sm:left-5 z-10 bg-burnt-coffee text-white px-2 rounded-lg">
            <CountdownPreview
              expiryDate={ticket.auctionEndTime}
              auctionStatus={ticket.auctionStatus}
            />
          </div>
          <img
            src={imageUrl}
            className="rounded-l-2xl w-full h-full object-cover z-0"
            onError={(e) => {
              // Evita loop infinito in caso PrevStation manchi anch’essa
              e.currentTarget.onerror = null;
              e.currentTarget.src = PrevStation;
            }}
          />
        </div>
        {/* Colonna destra con i dati del biglietto */}
        <div className="w-2/3 ">
          <div className="flex justify-between items-center sm:p-2">
            {/* Nome del treno e pulsanti */}
            <div className="flex items-center">
              <img
                src={Treno}
                className="w-6 pt-2 sm:w-8 pb-4 mr-4"
                alt="Treno Icona"
              />
              <h2 className="text-xl font-semibold uppercase font-heading sm:text-xl">
                {t("sell.train")}
              </h2>
            </div>
            <div className="flex space-x-2">
              <div onClick={handleFavoriteClick}>
                <FavoriteButton itemId={ticket._id} type="trains" />
              </div>
              <div onClick={handleShareClick}>
                <ShareButton url={cardUrl} />
              </div>
            </div>
          </div>

          <div className=" sm:space-y-1" style={getRotationStyle(isFlipped)}>
            <Link
              to={`/ticket/train/${ticket._id}`}
              state={{ user: ticket.user_id }}
            >
              <div
                className="font-semibold sm:text-lg "
                style={getTextRotationStyle(isFlipped)}
                // Applica lo stile di rotazione
              >
                {showReturnTrip
                  ? ticket.trainDepartureCityReturn.name
                  : ticket.trainDepartureCity.name}
              </div>
              <div style={getTextRotationStyle(isFlipped)}>
                {showReturnTrip ? ticket.changeReturn : ticket.change}{" "}
                {t("general.change")} •{" "}
                {showReturnTrip ? returnDuration : travelDuration}
              </div>
              <div
                className="font-semibold sm:text-lg"
                style={getTextRotationStyle(isFlipped)}
                // Applica lo stile di rotazione
              >
                {showReturnTrip
                  ? ticket.trainDestinationCityReturn.name
                  : ticket.trainDestinationCity.name}
              </div>
              <div
                className="flex items-center sm:text-base mb-4"
                style={getTextRotationStyle(isFlipped)}
              >
                {formatDate(
                  showReturnTrip
                    ? ticket.trainDateTimeReturnDeparture
                    : ticket.trainDateTimeDeparture
                )}{" "}
                •
                <img
                  src={Single}
                  className="w-6 h-6 mr-1"
                  alt="Alloggio Icona"
                />
                <div className="mr-4">{ticket.numberPeople}</div>
                <TrainLogo trainCompany={ticket.trainCompany} />
              </div>
            </Link>

            {/* Switch per mostrare andata/ritorno */}
            <div
              className="flex justify-between items-start  sm:pr-2"
              style={getRotationStyle(isFlipped)}
            >
              <div className="w-1/3">
                <div
                  className="font-semibold text-sm lg:text-lg uppercase font-heading text-start mt-1 "
                  // Applica lo stile di rotazione
                >
                  {ticket.trainDateTimeReturnDeparture
                    ? "A/R"
                    : t("sellTrain.onlyGone")}
                </div>
              </div>
              {ticket.trainDepartureCityReturn && (
                <div className="lg:w-1/3 w-full  flex justify-start">
                  <label className="switch-train">
                    <input
                      type="checkbox"
                      checked={showReturnTrip}
                      onChange={handleFlip} // Gestisci il cambio di stato
                      onClick={(e) => {
                        // Fermiamo la propagazione qui senza bloccare il cambio di stato
                        e.stopPropagation();
                      }}
                    />
                    <span className="slider-train round-train">
                      <span
                        className={`trip-label trip-label-andata ${
                          !showReturnTrip ? "active" : ""
                        }`}
                      >
                        {t("flightFields.gone")}
                      </span>
                      <span
                        className={`trip-label trip-label-ritorno ${
                          showReturnTrip ? "active" : ""
                        }`}
                      >
                        {t("flightFields.return2")}
                      </span>
                    </span>
                  </label>
                </div>
              )}
            </div>
            {/* Prezzo e sconto */}
            <div
              className="flex justify-end pr-1 sm:pr-4 pt-2"
              style={getRotationStyle(isFlipped)}
            >
              <span className="text-xl font-bold text-burnt-coffee pr-2">
                {convertCurrencySymbol(ticket.currency)}
                {highestOffer || displayPrice}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainCard;
